import { isDerivedStat } from './statValues'

/** Number of y-axis ticks: bottom, middle, top. */
const VALUE_TICK_COUNT = 3

/** Every period up to 12, every 4th up to 26, every 6th beyond. */
const tickStep = (count: number): number => {
  if (count <= 12) {
    return 1
  }
  if (count <= 26) {
    return 4
  }
  return 6
}

/**
 * Which periods of a progress series get an x-axis label, so a 49-month
 * series stays readable while a 12-month one labels every month. Shared by
 * the stat trend and the All Sports games-played trend so both thin the same
 * way. `maxLabels` (how many fit the drawn width) replaces the count steps.
 */
export const pickTickIndexes = (
  count: number,
  maxLabels: number = Infinity
): number[] => {
  // A chart that knows its width (a phone, maybe scrolling) passes how many
  // labels fit and is thinned by that alone; otherwise by the period count.
  const step = Number.isFinite(maxLabels)
    ? Math.max(1, Math.ceil(count / Math.max(1, maxLabels)))
    : tickStep(count)
  return Array.from({ length: count }, (_, index) => index).filter(
    (index) => index % step === 0
  )
}

/** Round steps a rate axis may use, per power of ten. */
const NICE_STEPS = [1, 2, 2.5, 5]

/** Drops float noise (0.30000000000000004) so ticks print cleanly. */
const roundTick = (value: number): number => Number(value.toFixed(10))

/**
 * The largest round step (1, 2, 2.5 or 5 × 10^k) no bigger than `raw`, so
 * ticks land on values a reader recognises (.500, 1.000) instead of the
 * padded domain edges (.497, 1.005).
 */
const niceStep = (raw: number): number => {
  const magnitude = 10 ** Math.floor(Math.log10(raw))
  const normalized = raw / magnitude
  const nice = [...NICE_STEPS].reverse().find((step) => step <= normalized)
  return (nice ?? 1) * magnitude
}

/** Every multiple of `step` inside [min, max]. */
const multiplesWithin = (min: number, max: number, step: number): number[] => {
  const first = Math.ceil(roundTick(min / step)) * step
  const ticks: number[] = []
  for (let tick = first; tick <= max + step * 1e-9; tick += step) {
    ticks.push(roundTick(tick))
  }
  return ticks
}

/**
 * Y-axis tick values across [min, max]. A rate stat (PTS/G, SV%…) gets round
 * values inside the domain (about three); a counted stat (games, goals,
 * points) gets whole numbers only, since half a game is meaningless —
 * duplicates from a small range collapse.
 */
export const buildValueTicks = (
  min: number,
  max: number,
  abbr: string
): number[] => {
  if (isDerivedStat(abbr)) {
    const span = max - min
    if (span <= 0) {
      return [roundTick(min)]
    }
    return multiplesWithin(min, max, niceStep(span / (VALUE_TICK_COUNT - 1)))
  }
  const step = (max - min) / (VALUE_TICK_COUNT - 1)
  const ticks = Array.from(
    { length: VALUE_TICK_COUNT },
    (_, index) => min + step * index
  )
  return Array.from(new Set(ticks.map((tick) => Math.floor(tick))))
}
